// Audit Log Service for SwipeSage Admin Panel
//
// Best-effort forensic record of every admin mutation. Each entry is
// append-only (enforced in firestore.rules) and self-attributed
// (actorUid must equal request.auth.uid).
//
// Writes are non-fatal: if the audit collection is unreachable we log
// to console but do NOT throw — observability shouldn't block the
// real mutation. Reads are admin-only, surfaced via the /audit-log
// page.
import {
  addDoc,
  collection,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getFirebaseFirestore } from '../firebase';
import { authService } from '../auth';

export type AuditAction = 'create' | 'update' | 'delete'
export type AuditEntityType =
  | 'card'
  | 'mitc'
  | 'mitc_session'
  | 'referral_link'
  | 'user'
  | 'offer'
  | 'offer_source'
  | 'news_brief'
  | 'grievance'
  | 'breach_notice'

export interface AuditChange {
  before: unknown
  after:  unknown
}

export interface AuditLogEntry {
  id:           string
  actorUid:     string
  actorEmail:   string | null
  action:       AuditAction
  entityType:   AuditEntityType
  entityId:     string
  entityLabel?: string                              // human-readable (card name, user email)
  changes?:     Record<string, AuditChange>         // per-field diff for updates
  metadata?:    Record<string, unknown>             // free-form context
  timestamp:    Date | null
}

export interface AuditFilters {
  entityType?: AuditEntityType
  action?:     AuditAction
  actorUid?:   string
  entityId?:   string
}

export interface AuditPage {
  data:    AuditLogEntry[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
  scannedCount: number
}

const COLLECTION = 'audit_log'
const BATCH_SIZE = 50
const MAX_SCANNED_PER_PAGE = 2000

export type AuditCursor = QueryDocumentSnapshot<DocumentData>

function matchesFilters(entry: AuditLogEntry, filters: AuditFilters): boolean {
  return (!filters.entityType || entry.entityType === filters.entityType)
    && (!filters.action || entry.action === filters.action)
    && (!filters.actorUid || entry.actorUid === filters.actorUid)
    && (!filters.entityId || entry.entityId === filters.entityId)
}

class AuditService {
  private db = getFirebaseFirestore()

  /**
   * Write an audit entry. Never throws — if Firestore is unreachable
   * or rules reject the write, log it and continue. The caller's
   * primary mutation already succeeded by the time we get here.
   */
  async writeAudit(
    entry: Omit<AuditLogEntry, 'id' | 'actorUid' | 'actorEmail' | 'timestamp'>,
  ): Promise<void> {
    try {
      const user = authService.getCurrentUser()
      if (!user) {
        // No signed-in admin — should not happen in normal flow.
        console.warn('[audit] skipping entry: no current user', entry)
        return
      }

      // Strip undefined values (Firestore rejects them) and any
      // accidental Function/Symbol values via a JSON round-trip on
      // the changes/metadata blocks.
      const safe = sanitize({
        actorUid:    user.uid,
        actorEmail:  user.email ?? null,
        action:      entry.action,
        entityType:  entry.entityType,
        entityId:    entry.entityId,
        entityLabel: entry.entityLabel,
        changes:     entry.changes,
        metadata:    entry.metadata,
        timestamp:   new Date(),
      })

      await addDoc(collection(this.db, COLLECTION), safe)
    } catch (err) {
      console.warn('[audit] writeAudit failed (continuing):', err)
    }
  }

  /**
   * Scan one timestamp-ordered cursor, then filter before filling the page.
   * Entries without timestamp (the separate account-deletion schema) are
   * excluded by Firestore's orderBy and are not represented as admin edits.
   * Firestore has no audit_log composite indexes for the filter combinations.
   * A sparse filter that would scan beyond the per-page bound fails visibly
   * rather than claiming that the result set is empty or complete.
   */
  async getEntries(
    filters: AuditFilters = {},
    pageSize: number = 20,
    lastDoc?: AuditCursor | null,
  ): Promise<AuditPage> {
    try {
      if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > BATCH_SIZE) {
        throw new Error(`Audit page size must be between 1 and ${BATCH_SIZE}.`)
      }

      const data: AuditLogEntry[] = []
      let scanCursor = lastDoc ?? null
      let lastMatch: AuditCursor | null = null
      let scanned = 0

      while (scanned < MAX_SCANNED_PER_PAGE) {
        const batchLimit = Math.min(BATCH_SIZE, MAX_SCANNED_PER_PAGE - scanned)
        const constraints = [
          orderBy('timestamp', 'desc'),
          orderBy(documentId(), 'desc'),
          ...(scanCursor ? [startAfter(scanCursor)] : []),
          limit(batchLimit),
        ]
        const snap = await getDocs(query(collection(this.db, COLLECTION), ...constraints))

        for (const doc of snap.docs) {
          scanned += 1
          const raw = doc.data()
          const entry = {
            ...raw,
            id: doc.id,
            timestamp: toDate(raw.timestamp),
          } as AuditLogEntry
          if (!matchesFilters(entry, filters)) continue
          if (data.length === pageSize) {
            return {data, lastDoc: lastMatch, hasMore: true, scannedCount: scanned}
          }
          data.push(entry)
          lastMatch = doc
        }

        if (snap.docs.length < batchLimit) {
          return {data, lastDoc: lastMatch, hasMore: false, scannedCount: scanned}
        }
        scanCursor = snap.docs[snap.docs.length - 1]
      }

      // Exactly hitting the bound can also mean the collection ended there.
      // Probe one more document before declaring the result incomplete.
      const tail = await getDocs(query(
        collection(this.db, COLLECTION),
        orderBy('timestamp', 'desc'),
        orderBy(documentId(), 'desc'),
        startAfter(scanCursor!),
        limit(1),
      ))
      if (tail.docs.length === 0) {
        return {data, lastDoc: lastMatch, hasMore: false, scannedCount: scanned}
      }
      throw new Error(
        `Audit filter reached the ${MAX_SCANNED_PER_PAGE}-entry scan limit without confirming the next page. The result is unavailable; a reviewed server-side index or query design is needed for larger histories.`,
      )
    } catch (err) {
      console.error('[audit] getEntries failed:', err)
      throw err
    }
  }
}

// ── helpers ────────────────────────────────────────────────────────

/**
 * Compute a shallow per-field diff between two objects, returning
 * only keys whose serialized representation differs. Skips keys
 * present in `after` but identical to `before`. Symmetric for
 * undefined-vs-missing (treated as equal).
 */
export function computeDiff(
  before: Record<string, unknown> | null | undefined,
  after:  Record<string, unknown>,
): Record<string, AuditChange> {
  const diff: Record<string, AuditChange> = {}
  const beforeObj = before ?? {}
  for (const key of Object.keys(after)) {
    const b = beforeObj[key]
    const a = after[key]
    if (!shallowEqual(b, a)) diff[key] = { before: b ?? null, after: a ?? null }
  }
  return diff
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

// Recursively strip undefined values; converts Date instances back
// to themselves (Firestore handles those natively).
function sanitize<T>(value: T): T {
  if (value === undefined || value === null) return value
  if (value instanceof Date) return value
  if (Array.isArray(value)) {
    return value
      .filter(v => v !== undefined)
      .map(v => sanitize(v)) as unknown as T
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue
      out[k] = sanitize(v)
    }
    return out as T
  }
  return value
}

function toDate(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  if (typeof value === 'object' && value !== null && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate()
    return isNaN(date.getTime()) ? null : date
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value)
    if (!isNaN(d.getTime())) return d
  }
  return null
}

export const auditService = new AuditService()
export default auditService
